// ================================================================
// PROPRIETARY & CONFIDENTIAL — Mt Forge Inc.
// Copyright (c) 2026 Mt Forge. All rights reserved.
// Unauthorized copying, reverse engineering, or AI-assisted
// reproduction of this software is strictly prohibited.
// Protected under US Copyright Law (17 U.S.C.) and international
// treaties. Patent Pending. License: PROPRIETARY
// ================================================================
import React, { useState } from "react";

const SALES_KPIS = [
  { name:"Monthly Recurring Revenue", value:"$87,500", target:"$100,000", trend:"up", change:"+12%", period:"vs last month" },
  { name:"New Customers", value:"8", target:"12", trend:"up", change:"+3", period:"this month" },
  { name:"Churn Rate", value:"2.1%", target:"<3%", trend:"down", change:"-0.4%", period:"vs last month" },
  { name:"Pipeline Value", value:"$245,000", target:"$300,000", trend:"up", change:"+18%", period:"vs last month" },
  { name:"Demo-to-Close Rate", value:"34%", target:"40%", trend:"up", change:"+5%", period:"vs last quarter" }
];

const DEV_KPIS = [
  { name:"Sprint Velocity", value:"42 pts", target:"45 pts", trend:"up", change:"+4 pts", period:"this sprint" },
  { name:"Open Bug Count", value:"14", target:"<10", trend:"down", change:"-3", period:"vs last week" },
  { name:"Deploy Frequency", value:"4.2/week", target:"5/week", trend:"up", change:"+0.8", period:"vs last month" },
  { name:"Platform Uptime", value:"99.87%", target:"99.9%", trend:"stable", change:"0%", period:"last 30 days" },
  { name:"Code Review Time", value:"4.2 hrs", target:"<6 hrs", trend:"down", change:"-1.1 hrs", period:"avg this week" }
];

const OPS_KPIS = [
  { name:"Support Tickets", value:"23", target:"<20", trend:"up", change:"+5", period:"open this week" },
  { name:"Avg Response Time", value:"2.4 hrs", target:"<4 hrs", trend:"down", change:"-0.8 hrs", period:"vs last week" },
  { name:"Customer Satisfaction", value:"4.5/5", target:"4.5/5", trend:"stable", change:"+0.1", period:"last 30 days" },
  { name:"Onboarding Time", value:"3.2 days", target:"<5 days", trend:"down", change:"-0.5 days", period:"avg this month" }
];

const FINANCE_KPIS = [
  { name:"Revenue (MTD)", value:"$112,000", target:"$130,000", trend:"up", change:"+23%", period:"vs last month" },
  { name:"Expenses (MTD)", value:"$78,500", target:"<$85,000", trend:"stable", change:"+2%", period:"vs last month" },
  { name:"Burn Rate", value:"$68,000/mo", target:"<$75,000", trend:"down", change:"-$4,000", period:"vs last month" },
  { name:"Runway", value:"18 months", target:">12 months", trend:"up", change:"+2 months", period:"vs last quarter" }
];

const PERIODS = ["Weekly","Monthly","Quarterly"];

export default function KPIDashboardPage() {
  const [period, setPeriod] = useState("Monthly");
  const [activeCategory, setActiveCategory] = useState("all");

  const categories = [
    { key:"all", label:"All KPIs" },
    { key:"sales", label:"Sales" },
    { key:"dev", label:"Development" },
    { key:"ops", label:"Operations" },
    { key:"finance", label:"Finance" }
  ];

  const trendIcon = (trend) => {
    if (trend === "up") return { symbol:"\u2191", color:"#10b981" };
    if (trend === "down") return { symbol:"\u2193", color:"#10b981" };
    return { symbol:"\u2192", color:"#94a3b8" };
  };

  const renderKPICard = (kpi, idx) => {
    const t = trendIcon(kpi.trend);
    return (
      <div key={idx} className="stat-card">
        <div className="stat-label">{kpi.name}</div>
        <div className="stat-value">{kpi.value}</div>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginTop:4}}>
          <span style={{fontSize:11,color:"#64748b"}}>Target: {kpi.target}</span>
          <span style={{fontSize:12,fontWeight:600,color:t.color}}>{t.symbol} {kpi.change}</span>
        </div>
        <div style={{fontSize:10,color:"#64748b",marginTop:2}}>{kpi.period}</div>
      </div>
    );
  };

  const renderSection = (title, kpis, color) => (
    <div className="card" style={{marginBottom:16}}>
      <div className="card-header">
        <h3 style={{color}}>{title}</h3>
        <span style={{fontSize:12,color:"#64748b"}}>{kpis.length} metrics</span>
      </div>
      <div className="stats-grid">
        {kpis.map((kpi, idx) => renderKPICard(kpi, idx))}
      </div>
    </div>
  );

  return (
    <div>
      <div className="page-header">
        <div><h1 className="page-title">KPI Dashboard</h1><p>Key Performance Indicators across all departments</p></div>
        <div style={{display:"flex",gap:8}}>
          {PERIODS.map(p => (
            <button key={p} className={"btn "+(period===p?"btn-primary":"btn-outline")} onClick={() => setPeriod(p)}>{p}</button>
          ))}
        </div>
      </div>

      {/* Top-level summary */}
      <div className="stats-grid">
        <div className="stat-card" style={{borderTop:"3px solid #10b981"}}>
          <div className="stat-label">Overall Health</div>
          <div className="stat-value" style={{color:"#10b981"}}>Good</div>
          <div style={{fontSize:11,color:"#64748b"}}>14 of 18 KPIs on target</div>
        </div>
        <div className="stat-card" style={{borderTop:"3px solid #3b82f6"}}>
          <div className="stat-label">MRR Growth</div>
          <div className="stat-value" style={{color:"#3b82f6"}}>+12%</div>
          <div style={{fontSize:11,color:"#64748b"}}>$87,500 / $100,000 target</div>
        </div>
        <div className="stat-card" style={{borderTop:"3px solid #10b981"}}>
          <div className="stat-label">Customer Satisfaction</div>
          <div className="stat-value" style={{color:"#10b981"}}>4.5/5</div>
          <div style={{fontSize:11,color:"#64748b"}}>Meeting target</div>
        </div>
        <div className="stat-card" style={{borderTop:"3px solid #eab308"}}>
          <div className="stat-label">Runway</div>
          <div className="stat-value">18 mo</div>
          <div style={{fontSize:11,color:"#64748b"}}>+2 months vs last quarter</div>
        </div>
      </div>

      {/* Category filter */}
      <div style={{display:"flex",gap:8,marginBottom:16}}>
        {categories.map(c => (
          <button key={c.key} className={"btn btn-sm "+(activeCategory===c.key?"btn-primary":"btn-outline")} onClick={() => setActiveCategory(c.key)}>{c.label}</button>
        ))}
      </div>

      {(activeCategory === "all" || activeCategory === "sales") && renderSection("Sales KPIs", SALES_KPIS, "#10b981")}
      {(activeCategory === "all" || activeCategory === "dev") && renderSection("Development KPIs", DEV_KPIS, "#3b82f6")}
      {(activeCategory === "all" || activeCategory === "ops") && renderSection("Operations KPIs", OPS_KPIS, "#eab308")}
      {(activeCategory === "all" || activeCategory === "finance") && renderSection("Finance KPIs", FINANCE_KPIS, "#a855f7")}

      {/* Target vs Actual */}
      <div className="card" style={{marginTop:16}}>
        <div className="card-header"><h3>Target vs Actual - Key Metrics</h3></div>
        <div className="chart-bar-container">
          {[
            { label:"MRR", actual:87.5, target:100, unit:"$K" },
            { label:"New Customers", actual:8, target:12, unit:"" },
            { label:"Sprint Velocity", actual:42, target:45, unit:"pts" },
            { label:"Uptime", actual:99.87, target:99.9, unit:"%" },
            { label:"Response Time", actual:76, target:100, unit:"%" }
          ].map(m => {
            const pct = Math.min((m.actual / m.target) * 100, 100);
            return (
              <div key={m.label} className="chart-bar-row">
                <span className="chart-bar-label" style={{width:120}}>{m.label}</span>
                <div className="chart-bar-track">
                  <div className={"chart-bar-fill"+(pct >= 90?"":" orange")} style={{width:pct+"%"}}>
                    {m.actual}{m.unit} / {m.target}{m.unit}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Trends */}
      <div className="grid-2" style={{marginTop:16}}>
        <div className="card">
          <div className="card-header"><h3>Improving Metrics</h3></div>
          {["MRR (+12%)","Pipeline Value (+18%)","Churn Rate (-0.4%)","Deploy Frequency (+0.8/week)","Burn Rate (-$4K/mo)"].map((m, i) => (
            <div key={i} style={{display:"flex",alignItems:"center",gap:8,padding:"8px 0",borderBottom:"1px solid #334155"}}>
              <span style={{color:"#10b981",fontWeight:700}}>{"\u2191"}</span>
              <span style={{fontSize:13,color:"#e2e8f0"}}>{m}</span>
            </div>
          ))}
        </div>
        <div className="card">
          <div className="card-header"><h3>Needs Attention</h3></div>
          {["New Customers (8/12 target)","Support Tickets (23 open, target <20)","Open Bugs (14, target <10)","Demo-to-Close (34%, target 40%)"].map((m, i) => (
            <div key={i} style={{display:"flex",alignItems:"center",gap:8,padding:"8px 0",borderBottom:"1px solid #334155"}}>
              <span style={{color:"#eab308",fontWeight:700}}>!</span>
              <span style={{fontSize:13,color:"#e2e8f0"}}>{m}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
